"use client";

import { type ReactNode } from "react";
import { Calendar, Clock, User, type LucideIcon } from "lucide-react";
import { WorkspaceAvatarDisplay } from "@/app/components/WorkspaceAvatarDisplay";
import { AeraImage } from "@/app/components/ui/AeraImage";
import type { WorkspaceAvatarSelection } from "@/lib/workspaceAvatar";

type WorkspaceEditSummaryCardProps = {
    name: string;
    avatar: WorkspaceAvatarSelection;
    createdAt?: string | null;
    updatedAt?: string | null;
    ownerDisplayName?: string | null;
    ownerAvatarUrl?: string | null;
    ownerLoading?: boolean;
    statusLabel?: string | null;
};

function statusBadgeClass(label: string): string {
    if (label === "ปิดใช้งาน") {
        return "bg-aera-redlight text-aera-red";
    }
    if (label === "เปิดใช้งาน") {
        return "bg-aera-greenlight text-aera-green";
    }
    return "bg-aera-white text-aera-charcoal";
}

function hasMetaValue(value?: string | null): value is string {
    return Boolean(value && value !== "-");
}

function MetaRow({
    icon: Icon,
    label,
    children,
}: {
    icon: LucideIcon;
    label: string;
    children: ReactNode;
}) {
    return (
        <li className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-aera-white border border-aera-lightgray/80 flex items-center justify-center shrink-0">
                <Icon size={15} className="text-aera-gray" strokeWidth={2} aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-[11px] text-aera-gray leading-none mb-0.5">{label}</p>
                <div className="text-sm text-aera-charcoal truncate">{children}</div>
            </div>
        </li>
    );
}

export function WorkspaceEditSummaryCard({
    name,
    avatar,
    createdAt,
    updatedAt,
    ownerDisplayName,
    ownerAvatarUrl,
    ownerLoading = false,
    statusLabel,
}: WorkspaceEditSummaryCardProps) {
    const ownerName = ownerLoading ? "กำลังโหลด…" : (ownerDisplayName?.trim() || "—");

    return (
        <div className="mt-5 overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-linear-to-b from-aera-white/90 to-aera-lightgray/80">
            <div className="p-4 flex items-start gap-4">
                <WorkspaceAvatarDisplay avatar={avatar} size={56} className="shrink-0" />
                <div className="min-w-0 flex-1 pt-1">
                    <h3 className="text-base font-semibold text-aera-charcoal truncate leading-tight">
                        {name || "เวิร์กสเปซ"}
                    </h3>
                    {statusLabel ? (
                        <span
                            className={[
                                "inline-block mt-1.5 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full",
                                statusBadgeClass(statusLabel),
                            ].join(" ")}
                        >
                            {statusLabel}
                        </span>
                    ) : null}
                </div>
            </div>

            <ul className="px-4 pb-4 space-y-2.5" aria-label="ข้อมูลเวิร์กสเปซ">
                <MetaRow icon={User} label="ผู้สร้าง">
                    <span className="inline-flex items-center gap-2 min-w-0">
                        <span className="w-5 h-5 rounded-full bg-aera-lightgray border border-aera-lightgray/80 overflow-hidden shrink-0 flex items-center justify-center">
                            {ownerAvatarUrl ? (
                                <AeraImage native src={ownerAvatarUrl} alt="" width={20} height={20} className="w-full h-full object-cover" />
                            ) : (
                                <User size={10} className="text-aera-gray" />
                            )}
                        </span>
                        <span className="truncate">{ownerName}</span>
                    </span>
                </MetaRow>
                {hasMetaValue(createdAt) ? (
                    <MetaRow icon={Calendar} label="สร้างเมื่อ">
                        {createdAt}
                    </MetaRow>
                ) : null}
                {hasMetaValue(updatedAt) ? (
                    <MetaRow icon={Clock} label="อัปเดตล่าสุด">
                        {updatedAt}
                    </MetaRow>
                ) : null}
            </ul>
        </div>
    );
}
